import { Controller } from "@hotwired/stimulus";

// Drives the admin "Grant Complimentary Access" 4-step modal
// (Recipients → Products → Notify → Review). The Products step also carries the CME
// eligibility switch (TMS-1627): on by default, off for people who earned credit elsewhere.
//
// Both pickers accumulate into a list of removable chips. Recipients come from the shared
// single-select async typeahead: each pick becomes a chip and the field clears itself for
// the next one. Products come from a multi-select app-select, whose selected options are
// the source of truth — its chips are re-rendered from them. All state lives on the client
// until Confirm posts one JSON payload; the modal fully resets on close.
//
// Recipients can instead come from an imported file (TMS-1735). The server reads it and
// answers with the column pickers (iMIS ID, email, and any TRUE/FALSE product columns) and a
// summary of how its rows matched; any change to a column re-previews. Review previews once
// more with the chosen products for the final counts, and Confirm posts the file itself, so
// the server matches it again rather than trusting a list from the browser.
export default class extends Controller {
  static targets = [
    "stepper", "stepPill", "step",
    "error", "errorMessage",
    "recipientChips", "productSelect", "productChips",
    "searchPanel", "importPanel", "fileInput", "importLoading", "importMapping", "importSummary",
    "importProductsNote", "productPicker", "importReview",
    "notifyDetail", "channelInApp", "channelEmail", "reason", "cmeEligible",
    "previewRecipients", "previewProducts", "previewCme", "previewNotify", "previewGrants",
    "backBtn", "nextBtn", "confirmBtn",
    "chipTemplate"
  ];

  static values = { url: String, importUrl: String, importPreviewUrl: String };

  connect() {
    this.currentStep = 1;
    this.importData = null;
    this.importLoading = false;
    this.previewSeq = 0;

    this._boundReset = this._reset.bind(this);
    this._boundSelect = this._onSelectChange.bind(this);
    this.element.addEventListener("hidden.bs.modal", this._boundReset);
    this.element.addEventListener("app-select:change", this._boundSelect);

    this._showStep();
  }

  disconnect() {
    this.element.removeEventListener("hidden.bs.modal", this._boundReset);
    this.element.removeEventListener("app-select:change", this._boundSelect);
  }

  // ── Delegated select handling ────────────────────────────────────────

  _onSelectChange(event) {
    if (event.target?.dataset?.role === "recipient") {
      this._onRecipientPicked(event);
      return;
    }

    if (event.target?.dataset?.role === "import-column") {
      this._previewImport();
      return;
    }

    if (this.hasProductSelectTarget && this.productSelectTarget.contains(event.target)) {
      this._renderProductChips();
    }
  }

  // ── Step navigation ──────────────────────────────────────────────────

  nextStep() {
    const error = this._validateStep(this.currentStep);
    if (error) {
      this._showError(error);
      return;
    }
    this._hideError();
    if (this.currentStep < 4) {
      this.currentStep += 1;
      this._showStep();
    }
  }

  prevStep() {
    this._hideError();
    if (this.currentStep > 1) {
      this.currentStep -= 1;
      this._showStep();
    }
  }

  _showStep() {
    this.stepTargets.forEach((el) => {
      el.classList.toggle("d-none", Number(el.dataset.step) !== this.currentStep);
    });
    this.stepPillTargets.forEach((pill) => {
      const step = Number(pill.dataset.step);
      pill.classList.toggle("is-active", step === this.currentStep);
      pill.classList.toggle("is-complete", step < this.currentStep);
      if (step === this.currentStep) {
        pill.setAttribute("aria-current", "step");
      } else {
        pill.removeAttribute("aria-current");
      }
    });

    this.backBtnTarget.classList.toggle("d-none", this.currentStep === 1);
    this.nextBtnTarget.classList.toggle("d-none", this.currentStep === 4);
    this.confirmBtnTarget.classList.toggle("d-none", this.currentStep !== 4);

    if (this.currentStep === 2) this._showProductSource();
    if (this.currentStep === 4) this._renderPreview();

    this._focusFirstField();
  }

  _focusFirstField() {
    const step = this.stepTargets.find((el) => Number(el.dataset.step) === this.currentStep);
    const field = step?.querySelector("input:not([type=hidden]), button.app-select-button");
    if (field) requestAnimationFrame(() => field.focus());
  }

  _validateStep(step) {
    if (step === 1 && this._importing()) return this._validateImport();
    if (step === 1 && this._recipients().length === 0) {
      return "Add at least one recipient.";
    }
    if (step === 2 && !this._productsFromFile() && this._products().length === 0) {
      return "Select at least one product.";
    }
    if (step === 3 && this._notifyEnabled() && this._notifyChannels().length === 0) {
      return "Choose at least one way to notify the recipients.";
    }
    return null;
  }

  // ── Step 1: recipients ───────────────────────────────────────────────

  // The typeahead holds one value at a time, so each pick is moved into a chip and the
  // field is emptied — otherwise the next search would replace the previous person.
  _onRecipientPicked(event) {
    const id = event.target.value;
    const label = event.detail?.label || "";
    this._clearAsyncTypeahead(event.target);
    if (!id) return;

    this._hideError();
    if (this._recipients().some((recipient) => recipient.id === id)) return;

    this.recipientChipsTarget.appendChild(this._buildChip(id, label));
  }

  onSourceChange() {
    const importing = this._importing();
    this._toggle(this.searchPanelTarget, !importing);
    this._toggle(this.importPanelTarget, importing);
    this._hideError();
  }

  // ── Step 1: import a file ────────────────────────────────────────────

  // A new file starts over: its columns are picked by the server, then shown.
  onFileChange() {
    this.importData = null;
    this.importMappingTarget.innerHTML = "";
    this.importSummaryTarget.innerHTML = "";
    this._hideError();
    if (this._file()) this._previewImport({ fresh: true });
  }

  _validateImport() {
    if (!this._file()) return "Choose a CSV or .xlsx file.";
    if (this.importLoading) return "The file is still being read.";
    if (!this.importData) return "The file could not be read. Choose another file.";
    if (this.importData.counts.matched === 0) return "No rows in the file match a hub account.";
    return null;
  }

  // Previews the file. `fresh` lets the server pick the columns and re-renders the column
  // pickers; otherwise the pickers' current choices are sent and only the summary changes.
  // A newer preview supersedes an older one still in flight.
  async _previewImport({ fresh = false, withProducts = false } = {}) {
    const seq = ++this.previewSeq;
    this.importLoading = true;
    this._toggle(this.importLoadingTarget, true);
    if (withProducts) this.confirmBtnTarget.disabled = true;

    let data;
    try {
      const response = await fetch(this.importPreviewUrlValue, {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": this._csrfToken() },
        body: this._importFormData({ fresh, withProducts })
      });
      data = await response.json();
    } catch (e) {
      data = { success: false, errors: ["Something went wrong. Please try again."] };
    }
    if (seq !== this.previewSeq) return null;

    this.importLoading = false;
    this._toggle(this.importLoadingTarget, false);

    if (!data.success) {
      this.importData = null;
      this.importSummaryTarget.innerHTML = "";
      if (fresh) this.importMappingTarget.innerHTML = "";
      this._showError((data.errors || ["The file could not be read."]).join(", "));
      return null;
    }

    this._hideError();
    this.importData = data;
    if (fresh) this.importMappingTarget.innerHTML = data.mapping_html;
    this.importSummaryTarget.innerHTML = data.summary_html;
    return data;
  }

  _importFormData({ fresh = false, withProducts = false } = {}) {
    const data = new FormData();
    data.append("complimentary_access_import[file]", this._file());
    data.append("complimentary_access_import[columns_chosen]", fresh ? "0" : "1");

    if (!fresh) {
      this._columnInputs().forEach((input) => {
        const { field, column } = input.dataset;
        if (field === "product") {
          if (input.value) data.append(`complimentary_access_import[columns][products][${column}]`, input.value);
        } else {
          data.append(`complimentary_access_import[columns][${field}]`, input.value);
        }
      });
    }

    if (withProducts) {
      this._products().forEach((product) => data.append("complimentary_access_import[payment_product_ids][]", product.id));
    }
    return data;
  }

  downloadIssues() {
    const csv = this.importData?.issues_csv;
    if (!csv) return;

    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = this.importData.issues_filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  // ── Step 2: products ─────────────────────────────────────────────────

  // An import with mapped product columns grants per row, so the picker steps aside.
  _showProductSource() {
    if (!this.hasProductPickerTarget) return;

    const fromFile = this._productsFromFile();
    this._toggle(this.productPickerTarget, !fromFile);
    this._toggle(this.importProductsNoteTarget, fromFile);
  }

  _renderProductChips() {
    this._hideError();
    this.productChipsTarget.innerHTML = "";
    this._selectedProductOptions().forEach((option) => {
      this.productChipsTarget.appendChild(this._buildChip(option.dataset.value, option.dataset.label));
    });
  }

  // ── Chips ────────────────────────────────────────────────────────────

  _buildChip(id, label) {
    const chip = this.chipTemplateTarget.content.firstElementChild.cloneNode(true);
    chip.dataset.id = id;
    chip.querySelector("[data-chip-label]").textContent = label;
    chip.querySelector(".complimentary-access-chip-remove").setAttribute("aria-label", `Remove ${label}`);
    return chip;
  }

  // A product chip mirrors a selected option, so removing it means deselecting that
  // option and letting the change event re-render the list.
  removeChip(event) {
    const chip = event.currentTarget.closest("[data-chip]");
    if (!chip) return;

    this._hideError();

    if (this.hasProductChipsTarget && this.productChipsTarget.contains(chip)) {
      this._productOption(chip.dataset.id)?.click();
      return;
    }

    chip.remove();
  }

  // ── Step 3: notify ───────────────────────────────────────────────────

  onNotifyEnabledChange() {
    const enabled = this._notifyEnabled();
    this._toggle(this.notifyDetailTarget, enabled);
    if (enabled) {
      this.channelInAppTarget.checked = true;
      this.channelEmailTarget.checked = true;
    }
    this._hideError();
  }

  onNotifyChannelChange() {
    this._hideError();
  }

  // ── Step 4: preview ──────────────────────────────────────────────────

  _renderPreview() {
    if (this._importing()) {
      this._renderImportPreview();
      return;
    }

    this._toggle(this.importReviewTarget, false);
    this.confirmBtnTarget.disabled = false;
    const recipients = this._recipients();
    const products = this._products();

    this.previewRecipientsTarget.textContent = recipients.map((recipient) => recipient.label).join(", ");
    this.previewProductsTarget.textContent = products.map((product) => product.label).join(", ");
    this.previewCmeTarget.textContent = this._cmeEligible() ? "Eligible to claim CME" : "Not eligible to claim CME";
    this.previewNotifyTarget.textContent = this._notifySummary();
    this.previewGrantsTarget.textContent =
      `${this._pluralize(recipients.length, "recipient")} × ${this._pluralize(products.length, "product")}`;
  }

  // The counts are only final once the chosen products are known, so Review previews again
  // and Confirm waits for it.
  async _renderImportPreview() {
    this.importReviewTarget.innerHTML = "";
    this._toggle(this.importReviewTarget, true);
    this.previewRecipientsTarget.textContent = "Checking the file…";
    this.previewProductsTarget.textContent = this._productsFromFile()
      ? "From the file's product columns"
      : this._products().map((product) => product.label).join(", ");
    this.previewCmeTarget.textContent = this._cmeEligible() ? "Eligible to claim CME" : "Not eligible to claim CME";
    this.previewNotifyTarget.textContent = this._notifySummary();
    this.previewGrantsTarget.textContent = "";

    const data = await this._previewImport({ withProducts: true });
    if (!data || this.currentStep !== 4) return;

    const people = `${data.counts.to_grant} ${data.counts.to_grant === 1 ? "person" : "people"}`;
    this.importReviewTarget.innerHTML = data.summary_html;
    this.previewRecipientsTarget.textContent = `${people} from ${this._file().name}`;
    this.previewGrantsTarget.textContent = people;

    if (data.counts.to_grant === 0) {
      this._showError("Nobody in the file can be granted the selected products.");
      return;
    }
    this.confirmBtnTarget.disabled = false;
  }

  _notifySummary() {
    if (!this._notifyEnabled()) return "No notification";
    const channels = this._notifyChannels();
    const parts = [];
    if (channels.includes("in_app")) parts.push("In-app");
    if (channels.includes("email")) parts.push("Email");
    return parts.length ? parts.join(" · ") : "No notification";
  }

  // ── Submit ───────────────────────────────────────────────────────────

  async submit() {
    if (this._importing()) {
      await this._submitImport();
      return;
    }

    const payload = {
      complimentary_access_grant: {
        recipient_ids: this._recipients().map((recipient) => recipient.id),
        payment_product_ids: this._products().map((product) => product.id),
        reason: this._reasonValue() || null,
        cme_eligible: this._cmeEligible() ? "1" : "0",
        notify: {
          enabled: this._notifyEnabled() ? "1" : "0",
          channels: this._notifyChannels()
        }
      }
    };

    await this._post(this.urlValue, JSON.stringify(payload), { "Content-Type": "application/json" });
  }

  async _submitImport() {
    const data = this._importFormData({ withProducts: true });
    const reason = this._reasonValue();
    if (reason) data.append("complimentary_access_import[reason]", reason);
    data.append("complimentary_access_import[cme_eligible]", this._cmeEligible() ? "1" : "0");
    data.append("complimentary_access_import[notify][enabled]", this._notifyEnabled() ? "1" : "0");
    this._notifyChannels().forEach((channel) => data.append("complimentary_access_import[notify][channels][]", channel));

    await this._post(this.importUrlValue, data);
  }

  async _post(url, body, headers = {}) {
    this.confirmBtnTarget.disabled = true;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": this._csrfToken(), ...headers },
        body
      });
      const data = await response.json();
      if (data.success) {
        window.location.assign(data.redirect_url);
      } else {
        this._showError((data.errors || ["Could not grant complimentary access."]).join(", "));
        this.confirmBtnTarget.disabled = false;
      }
    } catch (e) {
      this._showError("Something went wrong. Please try again.");
      this.confirmBtnTarget.disabled = false;
    }
  }

  // ── Reset ────────────────────────────────────────────────────────────

  _reset() {
    this.currentStep = 1;

    this.recipientChipsTarget.innerHTML = "";
    this._clearAsyncTypeahead(this._recipientInput);

    this._setRadio("complimentary_access[source]", "search");
    this.onSourceChange();
    this.previewSeq += 1;
    this.importLoading = false;
    this.importData = null;
    this.fileInputTarget.value = "";
    this.importMappingTarget.innerHTML = "";
    this.importSummaryTarget.innerHTML = "";
    this.importReviewTarget.innerHTML = "";
    this._toggle(this.importLoadingTarget, false);
    this._selectedProductOptions().forEach((option) => option.click());

    this._setRadio("complimentary_access[notify_enabled]", "no");
    this.channelInAppTarget.checked = false;
    this.channelEmailTarget.checked = false;
    this._toggle(this.notifyDetailTarget, false);
    if (this.hasReasonTarget) this.reasonTarget.value = "";
    if (this.hasCmeEligibleTarget) this.cmeEligibleTarget.checked = true;

    this.confirmBtnTarget.disabled = false;
    this._hideError();
    this._showStep();
  }

  // ── Helpers ──────────────────────────────────────────────────────────

  _importing() {
    return this.element.querySelector('input[name="complimentary_access[source]"]:checked')?.value === "import";
  }

  _file() {
    return this.fileInputTarget.files[0] || null;
  }

  _columnInputs() {
    return Array.from(this.importMappingTarget.querySelectorAll('[data-role="import-column"]'));
  }

  _productsFromFile() {
    return this._importing() && Boolean(this.importData?.product_column_mapped);
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }

  get _recipientInput() {
    return this.element.querySelector('[data-async-typeahead-target="hidden"][data-role="recipient"]');
  }

  _recipients() {
    return this._chipsIn(this.recipientChipsTarget);
  }

  _products() {
    return this.hasProductChipsTarget ? this._chipsIn(this.productChipsTarget) : [];
  }

  _chipsIn(container) {
    return Array.from(container.querySelectorAll("[data-chip]")).map((chip) => ({
      id: chip.dataset.id,
      label: chip.querySelector("[data-chip-label]").textContent
    }));
  }

  _selectedProductOptions() {
    if (!this.hasProductSelectTarget) return [];
    return Array.from(this.productSelectTarget.querySelectorAll(".app-select-option.app-select-option-selected"));
  }

  _productOption(id) {
    if (!this.hasProductSelectTarget) return null;
    return this.productSelectTarget.querySelector(`.app-select-option[data-value="${id}"]`);
  }

  _reasonValue() {
    return this.hasReasonTarget ? this.reasonTarget.value.trim() : "";
  }

  // No products to grant means no switch rendered; the server default (eligible) applies.
  _cmeEligible() {
    return this.hasCmeEligibleTarget ? this.cmeEligibleTarget.checked : true;
  }

  _notifyEnabled() {
    return this.element.querySelector('input[name="complimentary_access[notify_enabled]"]:checked')?.value === "yes";
  }

  _notifyChannels() {
    const channels = [];
    if (this.channelInAppTarget.checked) channels.push("in_app");
    if (this.channelEmailTarget.checked) channels.push("email");
    return channels;
  }

  _setRadio(name, value) {
    const input = this.element.querySelector(`input[name="${name}"][value="${value}"]`);
    if (input) input.checked = true;
  }

  _pluralize(count, noun) {
    return `${count} ${noun}${count === 1 ? "" : "s"}`;
  }

  _toggle(el, show) {
    if (el) el.classList.toggle("d-none", !show);
  }

  _showError(message) {
    this.errorMessageTarget.textContent = message;
    this.errorTarget.classList.remove("d-none");
  }

  _hideError() {
    this.errorTarget.classList.add("d-none");
  }

  _clearAsyncTypeahead(hiddenInput) {
    if (!hiddenInput) return;
    const root = hiddenInput.closest("[data-controller~='async-typeahead']");
    const controller =
      root && this.application.getControllerForElementAndIdentifier(root, "async-typeahead");
    if (controller) {
      controller.clear({ dispatch: false });
      return;
    }
    hiddenInput.value = "";
  }
}
